import React, {
  createElement,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { CustomControlValueType, DualSelectorProps, Item } from '.';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import { useIsRTL } from '../../locale';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { useFormatter } from '../../locale/formatting';
import { renderInlineCssVars, useDesignTokens } from '../../theme';
import { postfixTestId } from '../../util/testId';
import { useDirectSpeech } from '../DirectSpeech/DirectSpeechProvider';
import { useKeyboardShortcuts } from '../KeyboardShortcuts';
import { Label } from '../Label';
import { MessageDialog, useMessageDialogText } from '../MessageDialog';
import { Splitter } from '../Splitter';
import {
  DsAction,
  DsState,
  getDualSelectorReducer,
  isItemSelectable,
  MoveActionPayload,
} from './DataManagement';
import { DualSelectorLayout, isAlwaysSelectedItem } from './DualSelectorLayout/DualSelectorLayout';
import { ButtonDirection, MoveButton } from './MoveButton/MoveButton';
import bundle from './NovaComponents-DualSelector-gui-icu.properties';
import { ReorderButton, ReorderButtonDirection } from './ReorderLayout/ReorderLayout';
import { isNullish } from '@sas/nova-core/typeguards';
import { getLogger } from '@sas/nova-core/logging';

import cx from 'classnames';
import './DualSelector.css';
import cn from './DualSelector.css.json';

const useTextLocalizer = createUseTextLocalizer(bundle);

type ButtonState = {
  direction: ButtonDirection;
  disableOne: boolean;
  disableAll: boolean;
  disableUndo: boolean;
};

type ReorderButtonState = {
  disableUp: boolean;
  disableDown: boolean;
  disableDelete: boolean;
};

type TableType = 'source' | 'target';
const useHtmlId = createUseHtmlId('dual-selector');

const initialReorderButtonStates: ReorderButtonState = {
  disableUp: false,
  disableDown: false,
  disableDelete: false,
};

const initialDoubleClickState = { clicked: false, selectedRow: -1 };
const initialCustomControlValueType: CustomControlValueType = { value: '', errorStatus: 'none' };

const logger = getLogger('DualSelector');

export const DualSelector = (props: DualSelectorProps) => {
  const localize = useTextLocalizer();
  let sourceLabelWithCount, targetLabelWithCount, disableSourceFilter, disableTargetFilter;
  let sourceTableIsSelected = true;
  const formatter = useFormatter();
  const dualSelectorId = useHtmlId(4);
  const directSpeech = useDirectSpeech();
  const dt = useDesignTokens();
  const isRTL = useIsRTL();
  const { getActionsByKeyEvent } = useKeyboardShortcuts();
  const { CONTINUE, CANCEL } = useMessageDialogText();

  const tokens = renderInlineCssVars(
    {
      fg: dt.c.colorTextDefault,
      bd: dt.backgroundsDepthNova.standard.bd,
      bg: dt.backgroundsDepthNova.standard.bg,
      'spacing-s': dt.spacing.core.s,
    },
    'dualselector'
  );
  const actionButtonTokens = renderInlineCssVars(
    {
      spacing: dt.spacing.core.s,
    },
    'dualselector-action-button'
  );

  const initialMoveButtonState: ButtonState = useMemo(
    () => ({
      direction: 'add',
      disableOne: true,
      disableAll: props.disableAddAllButton ?? false,
      disableUndo: true,
    }),
    [props.disableAddAllButton]
  );
  /* -------------------------------------------
   *  Copy the attributes so that the original do not change
   * ---------------------------------------------*/
  const source = { ...props.sourceAttribute };
  const target = { ...props.targetAttribute };
  const targetCopy = { ...target };

  /* -------------------------------------------
   *  Set default values if not passed in props
   * ---------------------------------------------*/

  const sourceLabel = source[0].label ?? localize('source.default.label');
  const targetLabel = target[0].label ?? localize('target.default.label');
  const key = props.dataFieldKeys[0];
  const hideReorder = targetCopy[0].sortFunction ? true : !!props.hideReorder;
  const hideDelete = !props.renderCustomControl;

  /* -------------------------------------------
   *  Add alwaysSelected items to target.
   * ---------------------------------------------*/
  if (targetCopy[0].alwaysSelected) {
    targetCopy[0].alwaysSelected.forEach((item) => {
      item._alwaysSelected = true;
    });
  }
  if (targetCopy[0].data === undefined && targetCopy[0].alwaysSelected !== undefined) {
    targetCopy[0].data = targetCopy[0].alwaysSelected;
  } else if (targetCopy[0].data !== undefined && targetCopy[0].alwaysSelected !== undefined) {
    targetCopy[0].data = [...new Set(targetCopy[0].data.concat(targetCopy[0].alwaysSelected))];
  }

  let targetData = targetCopy[0].data
    ? targetCopy[0].data.map((item) => {
        return { ...item };
      })
    : [];
  let sourceData = source[0].data
    ? source[0].data.map((a) => {
        return { ...a };
      })
    : [];

  /* -------------------------------------------
   *  Define all state variables
   * ---------------------------------------------*/
  const [tableName, setTableName] = useState<TableType>('source');
  const [buttonState, setButtonState] = useState<ButtonState>(initialMoveButtonState);
  const [reorderButtonState, setReorderButtonState] = useState<ReorderButtonState>(
    initialReorderButtonStates
  );

  const [isOpen, setIsOpen] = useState(false);
  const removeAllRef = useRef(false);
  const doubleClickRef = useRef(initialDoubleClickState);
  const customValueRef = useRef<CustomControlValueType>(initialCustomControlValueType);
  const enableCustomAddBtnRef = useRef(false);

  const sourceLayoutRef = useRef<any>();
  const targetLayoutRef = useRef<any>();

  const reset = useCallback(() => {
    setTableName('source');
    setButtonState(initialMoveButtonState);
    setReorderButtonState(initialReorderButtonStates);
    setIsOpen(false);
    removeAllRef.current = false;
    doubleClickRef.current = initialDoubleClickState;
  }, [initialMoveButtonState]);

  const setCustomControlValue = (customValue: CustomControlValueType) => {
    if (customValue) {
      customValueRef.current = customValue;
      const regex = /^\s+$/;
      if (
        customValue.value !== '' &&
        !regex.test(customValue.value.toString()) &&
        customValue.errorStatus === 'none'
      ) {
        enableCustomAddBtnRef.current = true;
      } else {
        enableCustomAddBtnRef.current = false;
      }
    }
  };

  /* -------------------------------------------
   * Get selected rows from tablename = source/target
   * ---------------------------------------------*/
  const onSelectHandler = (
    selectedRowsValue: Array<string> | null | undefined,
    selectedTableName: TableType
  ) => {
    if (selectedRowsValue) {
      dsDispatch({
        type: 'selection',
        payload: { selectedRows: selectedRowsValue, tableName: selectedTableName },
      });
      setTableName(selectedTableName);
    }
  };

  /* -------------------------------------------
   * Directly move item from one table to another on doubleClick
   * ---------------------------------------------*/
  const displayStyle =
    props.mode === 'keep' && props.targetAttribute[0].displayStyle === 'single'
      ? 'single'
      : 'multiple';
  const onDoubleClickHandler = (selectedRow: number, selectedTableName: TableType) => {
    if (selectedTableName === 'source' && props.disableAddButton) {
      return;
    }
    // AlwaysSelected items should not move
    if (
      !(
        selectedTableName === 'target' &&
        dsState.alwaysSelected &&
        isAlwaysSelectedItem(
          dsState.targetFilteredData
            ? dsState.targetFilteredData[selectedRow]
            : dsState.targetData[selectedRow]
        )
      )
    ) {
      const direction = selectedTableName === 'source' ? 'add' : 'remove';
      if (direction === 'remove' && selectedTableName === 'target') {
        const item = targetData?.[selectedRow];
        if (!!item?._custom) {
          doubleClickRef.current = { clicked: true, selectedRow };
          setIsOpen(true);
          return;
        }
      }
      dsDispatch({
        type: direction,
        payload: {
          selectedRows: [selectedRow],
          isMoveAll: false,
          mode: props.mode,
          targetDisplayStyle: displayStyle,
        },
      });
      setTableName(selectedTableName);
    }
  };

  /* -------------------------------------------
   *   Get Updated Data after Move buttons press
   * ---------------------------------------------*/

  const dualSelectorReducer = useMemo(() => {
    return getDualSelectorReducer(props.mode, props.selectedItemsThreshold);
  }, [props.mode, props.selectedItemsThreshold]);

  const [dsState, dsDispatch] = useReducer<React.Reducer<DsState, DsAction>>(dualSelectorReducer, {
    sourceData: sourceData ?? [],
    targetData: targetData ?? [],
    currentActionQueue: [],
    alwaysSelected: targetCopy[0].alwaysSelected ?? [],
    onTargetDataChange: (items, reason) => {
      const cleanItems = removeInternalFields(items);
      if (reason) {
        props.onTargetChange(cleanItems, reason);
      } else {
        props.onTargetChange(cleanItems);
      }
    },
    focusAfterAction: true,
    customItems: [],
  });

  const shouldApplySelectedItemsThreshold =
    !isNullish(props.selectedItemsThreshold) && props.selectedItemsThreshold > 0;

  const countLeftToMeetThreshold = props.selectedItemsThreshold! - dsState.targetData.length;

  const compareFn = useCallback(
    (a: Item, b: Item) => {
      const s1 = a[props.dataFieldKeys[0]];
      const s2 = b[props.dataFieldKeys[0]];

      if (s1 > s2) {
        return 1;
      } else if (s1 < s2) {
        return -1;
      } else {
        return 0;
      }
    },
    [props.dataFieldKeys]
  );

  const sourceString = JSON.stringify([...(source[0].data ?? [])].sort(compareFn));
  const targetString = JSON.stringify([...(target[0].data ?? [])].sort(compareFn));

  useEffect(() => {
    const onTargetChange = props.onTargetChange;
    reset();
    sourceLayoutRef.current?.reset();
    targetLayoutRef.current?.reset();
    dsDispatch({
      type: 'reset',
      payload: {
        sourceData: source[0].data ?? [],
        targetData: target[0].data ?? [],
        alwaysSelected: target[0].alwaysSelected ?? [],
        currentActionQueue: [],
        onTargetDataChange: (items, reason) => {
          const cleanItems = removeInternalFields(items);
          if (reason) {
            onTargetChange(cleanItems, reason);
          } else {
            onTargetChange(cleanItems);
          }
        },
        itemSelections: undefined,
        sourceFilteredData: undefined,
        targetFilteredData: undefined,
        selectionTable: undefined,
        sourceSearchValue: undefined,
        targetSearchValue: undefined,
        key: undefined,
        narrator: undefined,
        focusAfterAction: true,
        customItems: [],
        isDataReset: true,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceString, targetString, props.selectedItemsThreshold]);

  /* -------------------------------------------
   * Enable / Disable filter
   * ---------------------------------------------*/

  if (
    dsState.sourceData &&
    dsState.sourceData.length === 0 &&
    dsState.targetData &&
    dsState.targetData.length > 0
  ) {
    disableSourceFilter = true;
  } else if (
    dsState.sourceData &&
    dsState.sourceData.length > 0 &&
    dsState.targetData &&
    dsState.targetData.length === 0
  ) {
    disableTargetFilter = true;
  }

  /* -------------------------------------------
   * Get updated length
   * ---------------------------------------------*/

  const getUpdatedLabel = (
    filteredData: Array<Item> | undefined,
    originalData: Array<Item>,
    label: string,
    isSource = false
  ) => {
    let updatedLabel;
    const originalCount =
      (props.mode === 'disable' || props.mode === 'keep') && isSource
        ? originalData.filter((item) => isItemSelectable(item)).length
        : originalData.filter((item) => !item.hasChildren).length;

    if (filteredData && filteredData.length < originalData.length) {
      const filteredCount =
        (props.mode === 'disable' || props.mode === 'keep') && isSource
          ? filteredData.filter((item) => isItemSelectable(item)).length
          : filteredData.filter((item) => !item.hasChildren).length;
      updatedLabel = localize('filtered.length.label', {
        label,
        filteredLength: formatter?.formatNumeric(filteredCount) ?? `${filteredCount}`,
        totalLength: formatter?.formatNumeric(originalCount) ?? `${originalCount}`,
      });
    } else if (props.mode === 'disable' && isSource) {
      updatedLabel = localize('availableItems.length.label', {
        label,
        availableItemsLength: formatter?.formatNumeric(originalCount) ?? `${originalCount}`,
        totalLength: formatter?.formatNumeric(originalData.length) ?? `${originalData.length}`,
      });
    } else {
      updatedLabel = localize('item.length.label', {
        label,
        length: formatter?.formatNumeric(originalCount) ?? `${originalCount}`,
      });
    }
    return updatedLabel;
  };

  /* -------------------------------------------
   * Change label and filter values
   * ---------------------------------------------*/
  if (dsState.sourceFilteredData || dsState.targetFilteredData) {
    if (dsState.sourceFilteredData) {
      sourceData = dsState.sourceFilteredData;
      sourceLabelWithCount = getUpdatedLabel(
        dsState.sourceFilteredData,
        dsState.sourceData,
        sourceLabel,
        true
      );
      targetData = dsState.targetFilteredData ?? dsState.targetData;
      targetLabelWithCount = getUpdatedLabel(
        dsState.targetFilteredData,
        dsState.targetData,
        targetLabel
      );
    }
    if (dsState.targetFilteredData) {
      sourceData = dsState.sourceFilteredData ?? dsState.sourceData;
      sourceLabelWithCount = getUpdatedLabel(
        dsState.sourceFilteredData,
        dsState.sourceData,
        sourceLabel,
        true
      );
      targetData = dsState.targetFilteredData;
      targetLabelWithCount = getUpdatedLabel(
        dsState.targetFilteredData,
        dsState.targetData,
        targetLabel
      );
    }
  } else {
    sourceData = dsState.sourceData;
    targetData = dsState.targetData;
    sourceLabelWithCount = getUpdatedLabel(undefined, dsState.sourceData, sourceLabel, true);
    targetLabelWithCount = getUpdatedLabel(undefined, dsState.targetData, targetLabel);
  }

  /* -------------------------------------------
   *   Reorder button enable and disable
   * ---------------------------------------------*/
  useEffect(() => {
    const reorderButtonState = {
      disableUp: true,
      disableDown: true,
      disableDelete: true,
    };

    const onlyCustomItemsSelected = (
      items: Array<Item>,
      selectedRows: string[],
      customItems: Array<Item> | undefined
    ) => {
      if (selectedRows.length === 0 || items.length === 0 || selectedRows[0] === '-1') {
        return false;
      }
      const selectedRowsNumbers = selectedRows.map((value) => parseInt(value));
      if (customItems && customItems.length > 0 && selectedRowsNumbers?.length > 0) {
        for (const itemIndex of selectedRowsNumbers) {
          if (!customItems.includes(items[itemIndex]) && !items[itemIndex]._custom) {
            return false;
          }
        }
        return true;
      }
      return false;
    };

    const selectedRows = dsState.itemSelections;
    if (tableName === 'target' && !sourceTableIsSelected) {
      if (displayStyle === 'multiple' || !haveDuplicatedItems(dsState.targetData)) {
        if (dsState.targetData.length > 0 && selectedRows && selectedRows.length > 0) {
          if (selectedRows && selectedRows.length > 0) {
            if (
              onlyCustomItemsSelected(
                dsState.targetFilteredData ?? dsState.targetData,
                selectedRows,
                dsState.customItems
              )
            ) {
              reorderButtonState.disableDelete = false;
            }
            reorderButtonState.disableUp = false;
            reorderButtonState.disableDown = false;
            if (dsState.targetFilteredData) {
              reorderButtonState.disableUp = true;
              reorderButtonState.disableDown = true;
            } else {
              for (let index = 0; index < selectedRows.length; index++) {
                if (selectedRows[index] === '0') {
                  reorderButtonState.disableUp = true;
                }
                if (selectedRows[index] === (dsState.targetData.length - 1).toString()) {
                  reorderButtonState.disableDown = true;
                }
              }
            }
          }
        } else if (
          dsState.targetData.length === 0 ||
          (dsState.targetData.length > 0 &&
            dsState.targetFilteredData &&
            dsState.targetFilteredData.length > 0)
        ) {
          reorderButtonState.disableUp = true;
          reorderButtonState.disableDown = true;
        }
      } else {
        if (
          onlyCustomItemsSelected(
            dsState.targetFilteredData ?? dsState.targetData,
            selectedRows ?? [],
            dsState.customItems
          )
        ) {
          reorderButtonState.disableDelete = false;
        }
        reorderButtonState.disableUp = true;
        reorderButtonState.disableDown = true;
      }
    }
    setReorderButtonState(reorderButtonState);
  }, [
    tableName,
    dsState.targetData,
    dsState.itemSelections,
    sourceTableIsSelected,
    dsState.targetFilteredData,
    dsState.customItems,
    props.dataFieldKeys,
    props.targetAttribute,
    displayStyle,
  ]);

  /* -------------------------------------------
   *  Highlight selected item
   * ---------------------------------------------*/
  if (dsState.itemSelections && dsState.itemSelections.length > 0) {
    if (
      dsState.currentActionQueue &&
      dsState.currentActionQueue.length > 0 &&
      !dsState.selectionTable
    ) {
      if (dsState.currentActionQueue[0].from === 'source') {
        sourceTableIsSelected = true;
      } else if (dsState.currentActionQueue[0].from === 'target') {
        sourceTableIsSelected = false;
      }
    } else {
      if (dsState.selectionTable === 'source') {
        sourceTableIsSelected = true;
      } else {
        sourceTableIsSelected = false;
      }
    }
  }

  /* -------------------------------------------
   *   Manage button enable and disable
   * ---------------------------------------------*/

  useEffect(() => {
    /* -------------------------------------------
     * Create message for directSpeech
     * ---------------------------------------------*/
    if (dsState.narrator) {
      let message = '';
      if (dsState.narrator.action === 'moveall') {
        message = localize(`afterAction.${dsState.narrator.direction}All.aria`);
      } else if (dsState.narrator.action === 'undo') {
        // UndoTo is needed since dsState.currentActionQueue is emptied on undo
        message = localize(`afterAction.undo.${dsState.narrator.undoTo}.aria`);
      } else if (dsState.narrator.action === 'move') {
        message = localize(`afterAction.${dsState.narrator.direction}.aria`, {
          noOfSelectedRows: dsState.narrator.selectedRows
            ? dsState.narrator.selectedRows.toString()
            : '0',
        });
      } else if (dsState.narrator.action === 'reorder') {
        message = localize(`afterAction.${dsState.narrator.direction}.aria`);
      } else if (dsState.narrator.action === 'customAdd') {
        message = localize('afterAction.customAdd.aria');
      }
      directSpeech?.notify(message);
    }

    const newButton: ButtonState = {
      direction: 'add',
      disableOne: true,
      disableAll: false,
      disableUndo: true,
    };

    const sourceLength = (dsState.sourceFilteredData ?? dsState.sourceData).filter((item) =>
      isItemSelectable(item)
    ).length;
    const targetLength = (dsState.targetFilteredData ?? dsState.targetData).length;
    const selectedRows = dsState.itemSelections ?? [];

    if (tableName === 'source') {
      if (sourceLength > 0) {
        if (selectedRows.length > 0) {
          newButton.disableOne = false;
          if (shouldApplySelectedItemsThreshold) {
            if (sourceLength > countLeftToMeetThreshold) {
              newButton.disableAll = true;
              if (selectedRows.length > countLeftToMeetThreshold) {
                newButton.disableOne = true;
              }
            }
          }
        } else {
          // Condition: sourceLength > 0 && selectedRows === 0
          if (shouldApplySelectedItemsThreshold) {
            if (sourceLength > countLeftToMeetThreshold) {
              newButton.disableAll = true;
            }
          }
          newButton.disableOne = true;
        }
      } else {
        // Condition: sourceLength === 0
        if (!Array.isArray(dsState.sourceFilteredData) && targetLength > 0) {
          newButton.direction = 'remove';
          newButton.disableAll =
            Array.isArray(dsState.alwaysSelected) && dsState.alwaysSelected.length > 0;
        } else {
          // Condition: sourceLength === 0 && sourceFilter applied
          newButton.disableAll = true;
        }
      }
    } else if (tableName === 'target') {
      newButton.direction = 'remove';
      if (targetLength > 0) {
        if (selectedRows.length > 0) {
          newButton.disableOne = false;
          if (Array.isArray(dsState.alwaysSelected) && dsState.alwaysSelected.length > 0) {
            const selectedData = dsState.targetFilteredData ?? dsState.targetData;

            const alwaysSelectedItems = selectedData.filter((item) => isAlwaysSelectedItem(item));
            newButton.disableAll = alwaysSelectedItems.length > 0 ? true : false;

            // iterating to see if any of the selected rows are alwaysSelected ones.
            for (const selectedValue of selectedRows.values()) {
              const targetItem = selectedData[Number(selectedValue)];
              if (targetItem && isAlwaysSelectedItem(targetItem)) {
                newButton.disableOne = true;
                break;
              }
            }
          }
        } else {
          // Condition: targetLength > 0 && selectedRows === 0
          if (Array.isArray(dsState.alwaysSelected) && dsState.alwaysSelected.length > 0) {
            const selectedData = dsState.targetFilteredData ?? dsState.targetData;
            const alwaysSelectedItems = selectedData.filter((item) => isAlwaysSelectedItem(item));
            newButton.disableAll = alwaysSelectedItems.length > 0 ? true : false;
          }
        }
      } else {
        // Condition: targetLength === 0
        if (!Array.isArray(dsState.targetFilteredData) && sourceLength > 0) {
          newButton.direction = 'add';
          if (shouldApplySelectedItemsThreshold)
            newButton.disableAll = sourceLength > countLeftToMeetThreshold;
        } else {
          // Condition: targetLength === 0 && targetFilter applied
          newButton.disableAll = true;
        }
      }
    }

    /* -------------------------------------------
     *   Handle Undo actions
     * ---------------------------------------------
     */

    const currentActionQueue = dsState.currentActionQueue;
    if (currentActionQueue && currentActionQueue.length > 0) {
      newButton.disableUndo = false;
      if (
        currentActionQueue[0].from === 'source' &&
        !Array.isArray(dsState.sourceFilteredData) &&
        sourceLength === 0
      ) {
        newButton.direction = 'remove';
      } else if (
        currentActionQueue[0].from === 'target' &&
        !Array.isArray(dsState.targetFilteredData) &&
        targetLength === 0
      ) {
        newButton.direction = 'add';
      }
    } else {
      if (sourceLength > 0 && tableName === 'source') {
        newButton.direction = 'add';
      } else if (targetLength > 0 && tableName === 'target') {
        newButton.direction = 'remove';
      }
    }
    if (newButton.direction === 'add') {
      newButton.disableOne = props.disableAddButton ? true : newButton.disableOne;
      newButton.disableAll = props.disableAddAllButton ? true : newButton.disableAll;
    }
    setButtonState(newButton);
  }, [
    key,
    dsState,
    tableName,
    sourceTableIsSelected,
    directSpeech,
    localize,
    props.mode,
    shouldApplySelectedItemsThreshold,
    countLeftToMeetThreshold,
    props.disableAddButton,
    props.disableAddAllButton,
  ]);

  /* -------------------------------------------
   *   Call and display MoveButton based on direction
   * ---------------------------------------------*/

  const makeAction = (direction: ButtonDirection, isMoveAll = false, disable = false) => {
    let newDirection = direction as string;
    if (isMoveAll) {
      newDirection = `${direction}all`;
    } else if (direction === 'undo') {
      if (dsState.currentActionQueue[0] && dsState.currentActionQueue[0].from === 'source') {
        newDirection = `undoaddaction`;
      } else if (dsState.currentActionQueue[0] && dsState.currentActionQueue[0].from === 'target') {
        newDirection = 'undoremoveaction';
      }
    }

    let buttonAriaLabel = localize(`moveButton.${newDirection}.button.aria`, {
      targetListLabel: targetLabel,
    });
    if (newDirection === 'custom_add' && props.customAddButtonAriaLabel) {
      buttonAriaLabel = props.customAddButtonAriaLabel;
    }
    return (
      <MoveButton
        direction={direction}
        isMoveAll={isMoveAll}
        onPress={onMoveButtonClick}
        disabled={disable}
        ariaLabel={buttonAriaLabel}
        customAddButtonTooltip={props.customAddButtonTooltip}
        testId={postfixTestId(props.testId, 'moveButton')}
      />
    );
  };

  /* -------------------------------------------
   *  Callback on button click
   * ---------------------------------------------*/
  const onActionClick = (
    direction: ButtonDirection | ReorderButtonDirection,
    isMoveAll = false
  ) => {
    let dsPayload: MoveActionPayload;
    if (direction === 'custom_add') {
      const customKey = props.dataFieldKeys[0];
      const item: Item = { [customKey]: customValueRef.current.value, _custom: true };
      dsPayload = {
        selectedRows: item,
        isMoveAll,
        mode: props.mode,
        targetDisplayStyle: 'multiple',
      };
      enableCustomAddBtnRef.current = false;
    } else {
      dsPayload = {
        selectedRows: dsState.itemSelections ?? [],
        isMoveAll,
        mode: props.mode,
        targetDisplayStyle: displayStyle,
      };
    }

    if (direction === 'undo') {
      if (Array.isArray(dsState.currentActionQueue) && dsState.currentActionQueue.length > 0) {
        if (
          dsState.currentActionQueue[0].from === 'source' ||
          dsState.currentActionQueue[0].from === 'target'
        ) {
          setTableName(dsState.currentActionQueue[0].from);
        }
      }
    }

    dsDispatch({
      type: direction,
      payload: dsPayload,
    });
  };

  const onMoveButtonClick = (
    direction: ButtonDirection | ReorderButtonDirection,
    isMoveAll = false
  ) => {
    if (direction === 'remove') {
      let selectedItemsNum: Array<number> = [];
      let selectedItems;
      let targetItems: Item[] = [];
      let targetData: Item[] = [];
      if (dsState.targetFilteredData) {
        targetData = dsState.targetFilteredData;
      } else {
        targetData = dsState.targetData;
      }

      if (dsState.itemSelections && dsState.itemSelections.length > 0) {
        // handle remove
        if (!isMoveAll) {
          selectedItems = dsState.itemSelections;
          selectedItemsNum = selectedItems.map((value) => parseInt(value));
          for (const itemIndex of selectedItemsNum) {
            targetItems.push(targetData[itemIndex]);
          }
        } else {
          targetItems = targetData;
          removeAllRef.current = true;
        }
      } else if (isMoveAll === true) {
        // handle remove all
        targetItems = targetData;
        removeAllRef.current = true;
      }

      // check if targetItems has any custom item
      if (!!targetItems.find((item) => item._custom)) {
        setIsOpen(true);
        return;
      }
    }
    onActionClick(direction, isMoveAll);
  };
  /* -------------------------------------------
   *   Add buttons based on direction
   * ---------------------------------------------*/
  const addButtons = (
    <div className={cn.actionButtonPanel} style={actionButtonTokens}>
      {makeAction('undo', false, buttonState.disableUndo)}
      {(!props.hideAddButton || buttonState.direction === 'remove') &&
        makeAction(buttonState.direction, false, buttonState.disableOne)}
      {(!props.hideAddAllButton || buttonState.direction === 'remove') &&
        makeAction(buttonState.direction, true, buttonState.disableAll)}
      {props.renderCustomControl &&
        makeAction(
          'custom_add',
          false,
          (shouldApplySelectedItemsThreshold && countLeftToMeetThreshold <= 0) ||
            !enableCustomAddBtnRef.current
        )}
    </div>
  );

  /* -------------------------------------------
   * Filter data as per searchValue
   * ---------------------------------------------*/
  const onFilterValue = (searchValue: any, tableName: TableType) => {
    setTableName(tableName);
    dsDispatch({
      type: 'filter',
      payload: { table: tableName, searchValue, key },
    });
  };

  const keydownhandler = (event: React.KeyboardEvent<HTMLDivElement>, tableName: TableType) => {
    const isMoveAll = event.shiftKey && event.ctrlKey;
    if (isMoveAll && buttonState.disableAll) {
      return;
    }
    const { actions } = getActionsByKeyEvent(event.nativeEvent);
    if (actions.includes('undo') && !buttonState.disableUndo) {
      dsDispatch({
        type: 'undo',
        payload: { selectedRows: dsState.itemSelections ?? [], isMoveAll: false, mode: props.mode },
      });
      return;
    }
    switch (event.key) {
      case 'ArrowRight':
        if ((tableName === 'source' && !isRTL) || (tableName === 'target' && isRTL)) {
          if (
            !isMoveAll &&
            ((dsState.itemSelections && dsState.selectionTable !== tableName) ||
              buttonState.disableOne)
          ) {
            break;
          }

          if (
            haveCustomItems(
              dsState.itemSelections,
              dsState.targetFilteredData ?? dsState.targetData
            ) &&
            isRTL
          ) {
            setIsOpen(true);
            return;
          }

          dsDispatch({
            type: isRTL ? 'remove' : 'add',
            payload: {
              selectedRows: dsState.itemSelections ?? [],
              isMoveAll,
              mode: props.mode,
              targetDisplayStyle: displayStyle,
            },
          });
        }
        break;
      case 'ArrowLeft':
        if ((tableName === 'target' && !isRTL) || (tableName === 'source' && isRTL)) {
          if (
            !isMoveAll &&
            ((dsState.itemSelections && dsState.selectionTable !== tableName) ||
              buttonState.disableOne)
          ) {
            break;
          }

          if (
            haveCustomItems(
              dsState.itemSelections,
              dsState.targetFilteredData ?? dsState.targetData
            ) &&
            !isRTL
          ) {
            setIsOpen(true);
            return;
          }

          dsDispatch({
            type: isRTL ? 'add' : 'remove',
            payload: {
              selectedRows: dsState.itemSelections ?? [],
              isMoveAll,
              mode: props.mode,
              targetDisplayStyle: displayStyle,
            },
          });
        }
        break;
      case 'ArrowUp':
        if (
          tableName === 'target' &&
          !sourceTableIsSelected &&
          event.altKey &&
          event.shiftKey &&
          !reorderButtonState.disableUp
        ) {
          dsDispatch({
            type: 'up',
            payload: {
              selectedRows: dsState.itemSelections ?? [],
              isMoveAll: false,
              mode: props.mode,
              targetDisplayStyle: displayStyle,
            },
          });
        }
        break;
      case 'ArrowDown':
        if (
          tableName === 'target' &&
          !sourceTableIsSelected &&
          event.altKey &&
          event.shiftKey &&
          !reorderButtonState.disableDown
        ) {
          dsDispatch({
            type: 'down',
            payload: {
              selectedRows: dsState.itemSelections ?? [],
              isMoveAll: false,
              mode: props.mode,
              targetDisplayStyle: displayStyle,
            },
          });
        }
        break;
    }
  };

  const getWarningDialogTitle = () => {
    if (removeAllRef.current) {
      return localize('moveButton.removeAllButton.warningDialog.title');
    } else {
      return localize('moveButton.removeButton.warningDialog.title');
    }
  };

  const getWarningDialogMessage = () => {
    if (removeAllRef.current) {
      return localize('moveButton.removeAllButton.warningDialog.msg');
    } else {
      return localize('moveButton.removeButton.warningDialog.msg');
    }
  };

  useEffect(() => {
    if (
      shouldApplySelectedItemsThreshold &&
      dsState.targetData.length > props.selectedItemsThreshold!
    )
      logger.error(
        `There are ${
          dsState.targetData.length
        } items selected by default which exceeds the set threshold limit of ${props.selectedItemsThreshold!}.`
      );
  }, [shouldApplySelectedItemsThreshold, props.selectedItemsThreshold, dsState.targetData]);

  return (
    <div
      className={cn.container}
      style={{ ...tokens, ...props.style }}
      role="region"
      data-testid={props.testId}
    >
      <Splitter
        style={{ height: '100%', width: '100%' }}
        sections={[{ minSize: 200, defaultSize: '50%' }, { minSize: 200 }]}
        hideSplitterBar
        testId={postfixTestId(props.testId, 'Splitter')} // eslint-disable-line @sas/i18n/jsx-to-localize
      >
        <div className={cn.sourcePanel}>
          <div className={cn.contents}>
            <Label
              className={cn.contentTitle}
              wrapText={false}
              id={dualSelectorId[1]}
              testId={postfixTestId(props.testId, 'sourceLengthLabel')}
            >
              {sourceLabelWithCount}
            </Label>

            <div
              className={cn.contentList}
              onKeyDown={(event) => keydownhandler(event, 'source')}
              aria-labelledby={`${dualSelectorId[0]} ${dualSelectorId[1]}`}
            >
              <DualSelectorLayout
                testId={postfixTestId(props.testId, 'sourceLayout')}
                ref={sourceLayoutRef}
                tableName="source"
                type={props.type}
                mode={props.mode}
                dataFieldKeys={props.dataFieldKeys}
                data={sourceData ?? []}
                hideSearch={props.sourceAttribute[0].hideSearch ?? true}
                selectionCallback={onSelectHandler}
                selections={sourceTableIsSelected ? dsState.itemSelections : []}
                disableFilter={disableSourceFilter ?? false}
                filterValue={onFilterValue}
                onDoubleClick={onDoubleClickHandler}
                focusAfterAction={dsState.focusAfterAction}
                searchAriaLabel={localize('searchField.aria', { listLabel: sourceLabel })}
                descriptionField={props.descriptionField}
                zeroStateText={props.sourceAttribute[0].zeroStateText}
                isDataReset={dsState.isDataReset}
                sortFunction={source[0].sortFunction}
                disableSort={source[0].disableSort}
                numberOfResults={
                  dsState.sourceFilteredData && dsState.sourceFilteredData?.length >= 0
                    ? dsState.sourceFilteredData?.length
                    : null
                }
              />
            </div>
            {props.renderCustomControl && (
              <div className={cn.customField}>
                {props.renderCustomControl(
                  setCustomControlValue,
                  shouldApplySelectedItemsThreshold ? countLeftToMeetThreshold > 0 : true
                )}
              </div>
            )}
          </div>
          {addButtons}
        </div>
        <div className={cn.targetPanel}>
          <div className={cn.contents}>
            <Label
              className={cn.contentTitle}
              wrapText={false}
              id={dualSelectorId[3]}
              testId={postfixTestId(props.testId, 'targetLengthLabel')}
            >
              {targetLabelWithCount}
            </Label>

            <div
              className={cx(cn.contentList)}
              onKeyDown={(event) => keydownhandler(event, 'target')}
              aria-labelledby={`${dualSelectorId[2]} ${dualSelectorId[3]}`}
            >
              <DualSelectorLayout
                testId={postfixTestId(props.testId, 'targetLayout')}
                ref={targetLayoutRef}
                tableName="target"
                type={'list'}
                mode={props.mode}
                dataFieldKeys={props.dataFieldKeys}
                data={targetData ?? []}
                displayStyle={props.targetAttribute[0].displayStyle}
                hideSearch={props.targetAttribute[0].hideSearch ?? true}
                alwaysSelected={dsState.alwaysSelected}
                selectionCallback={onSelectHandler}
                selections={!sourceTableIsSelected ? dsState.itemSelections : []}
                disableFilter={disableTargetFilter ?? false}
                filterValue={onFilterValue}
                onDoubleClick={onDoubleClickHandler}
                focusAfterAction={dsState.focusAfterAction}
                searchAriaLabel={localize('searchField.aria', { listLabel: targetLabel })}
                descriptionField={props.descriptionField}
                customItems={dsState.customItems}
                zeroStateText={props.targetAttribute[0].zeroStateText}
                isDataReset={dsState.isDataReset}
                sortFunction={target[0].sortFunction}
                numberOfResults={
                  dsState.targetFilteredData && dsState.targetFilteredData?.length >= 0
                    ? dsState.targetFilteredData?.length
                    : null
                }
              />
            </div>
          </div>
          {!hideReorder || !hideDelete ? (
            <div className={cn.actionButtonPanel} style={actionButtonTokens}>
              <ReorderButton
                onPress={onActionClick}
                reorderButtonState={reorderButtonState}
                targetLabel={targetLabel}
                testId={postfixTestId(props.testId, 'reorderButton')}
                hideReorder={hideReorder}
                hideDelete={hideDelete}
              />
            </div>
          ) : null}
        </div>
      </Splitter>
      <MessageDialog
        type="warning"
        isOpen={isOpen}
        title={getWarningDialogTitle()}
        text={getWarningDialogMessage()}
        firstAction={{
          text: CONTINUE,
          onPress: () => {
            if (doubleClickRef.current.clicked) {
              dsDispatch({
                type: 'remove',
                payload: {
                  selectedRows: [doubleClickRef.current.selectedRow],
                  isMoveAll: false,
                  mode: props.mode,
                  targetDisplayStyle: displayStyle,
                },
              });
              doubleClickRef.current = { clicked: false, selectedRow: -1 };
            } else if (removeAllRef.current) {
              onActionClick('remove', true);
              removeAllRef.current = false;
            } else {
              onActionClick('remove');
            }
            setIsOpen(false);
          },
        }}
        dismissAction={{
          text: CANCEL,
          onPress: () => {
            if (doubleClickRef.current.selectedRow !== -1) {
              onSelectHandler([doubleClickRef.current.selectedRow.toString()], 'target');
            }
            doubleClickRef.current = { clicked: false, selectedRow: -1 };
            removeAllRef.current = false;
            setIsOpen(false);
          },
          isDefault: true,
        }}
      />
    </div>
  );
};

function removeInternalFields(items: Array<Item>) {
  return items.map((item) => {
    const { _custom, _disabled, _alwaysSelected, ...rest } = item;
    return rest;
  });
}

function haveDuplicatedItems(data: Array<Item>) {
  const clone = [...data];

  const index = clone.findIndex((item) => {
    const firstIndex = data.indexOf(item);
    const lastIndex = data.lastIndexOf(item);
    return firstIndex !== data.length - lastIndex - 1;
  });

  return index > -1;
}

function haveCustomItems(selectionRows: string[] | undefined, items: Array<Item>) {
  return !!selectionRows?.find((row) => items?.[parseInt(row)]?._custom);
}
